import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { shadows } from "../../config/styleConfig";
import { ImageUpload } from "../ui/ImageUpload";
import {
  TextField,
  ColorField,
  SliderField,
  CheckboxField,
  SettingsSection,
} from "../ui/form/SettingsField";

export interface FeatureCardProps {
  title: string;
  description: string;
  imageUrl?: string;
  imageHeight: number;
  category?: string;
  categoryColor: string;
  link?: string;
  linkText: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: number;
  showShadow: boolean;
  textFontFamily?: string;
}

export const FeatureCard = ({
  title = "Feature Title",
  description = "Description of this feature",
  imageUrl,
  imageHeight = 60,
  category,
  categoryColor = "#F2819F",
  link,
  linkText = "Learn more",
  backgroundColor = "#ffffff",
  textColor = "#2C2C2B",
  borderRadius = 12,
  showShadow = false,
  textFontFamily,
}: FeatureCardProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { inEditor } = useEditor((state) => ({
    inEditor: true,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (inEditor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const categoryFontSize = isMobile ? "0.65rem" : "0.75rem";
  const padding = isMobile ? 16 : 20;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        bgcolor: backgroundColor,
        borderRadius: `${borderRadius}px`,
        boxShadow: showShadow ? "0px 0px 20px 0px rgba(0, 0, 0, 0.15)" : "none",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        minHeight: isMobile ? 190 : 195,
        ...(selected && selectedOutlineSx),
      }}
    >
      {/* Content: Title + Description */}
      <Box
        sx={{
          p: `${padding}px`,
          pb: `${Math.round(padding * 0.5)}px`,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          pr: imageUrl ? `${padding + 60}px` : `${padding}px`,
        }}
      >
        <Element
          id="card-title"
          is={Text}
          text={title}
          fontSize={24}
          fontWeight="bold"
          tagName="h4"
          color={textColor}
          fontFamily={textFontFamily}
        />
        <Element
          id="card-desc"
          is={Text}
          text={description}
          fontSize={16}
          tagName="p"
          color={textColor}
          fontFamily={textFontFamily}
        />
      </Box>

      {/* Image — bottom right decorative */}
      {imageUrl && (
        <Box
          component="img"
          src={imageUrl}
          alt={title}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 8,
            width: `${(imageHeight || 60) + 36}px`,
            height: "auto",
            maxHeight: `${(imageHeight || 60) + 36}px`,
            objectFit: "contain",
          }}
        />
      )}

      {/* Link */}
      {link && (
        <Box sx={{ px: `${padding}px`, pt: "4px" }}>
          <Box
            component="a"
            href={link}
            onClick={handleLinkClick}
            sx={{
              fontSize: isMobile ? "0.8rem" : "0.875rem",
              fontWeight: 600,
              color: categoryColor,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {linkText} &gt;
          </Box>
        </Box>
      )}

      {/* Category badge — absolute bottom left */}
      {category && (
        <Box
          sx={{
            position: "absolute",
            bottom: padding,
            left: padding,
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              background: `linear-gradient(90deg, ${categoryColor}22 0%, ${categoryColor}44 100%)`,
              color: categoryColor,
              fontSize: categoryFontSize,
              fontWeight: 600,
              px: "12px",
              py: "4px",
              borderRadius: "999px",
              lineHeight: 1.4,
              whiteSpace: "nowrap",
            }}
          >
            {category}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const FeatureCardSettings = () => {
  const {
    actions: { setProp },
    imageUrl,
    imageHeight,
    category,
    categoryColor,
    link,
    linkText,
    backgroundColor,
    textColor,
    borderRadius,
    showShadow,
  } = useNode((node) => ({
    imageUrl: node.data.props.imageUrl,
    imageHeight: node.data.props.imageHeight,
    category: node.data.props.category,
    categoryColor: node.data.props.categoryColor,
    link: node.data.props.link,
    linkText: node.data.props.linkText,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    borderRadius: node.data.props.borderRadius,
    showShadow: node.data.props.showShadow,
  }));

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <ImageUpload
        label="Feature Image"
        value={imageUrl || ""}
        onChange={(url) => setProp((props: FeatureCardProps) => (props.imageUrl = url || undefined))}
        placeholder="Enter URL or upload image"
      />

      <SliderField
        label="Image Height"
        value={imageHeight}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.imageHeight = v))}
        min={100}
        max={400}
        unit="px"
      />

      <TextField
        label="Category"
        value={category || ""}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.category = v || undefined))}
        placeholder="Skincare"
      />

      <ColorField
        label="Category Color"
        value={categoryColor}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.categoryColor = v))}
      />

      <TextField
        label="Link URL"
        value={link || ""}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.link = v || undefined))}
        placeholder="/features/skincare"
      />

      <TextField
        label="Link Text"
        value={linkText}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.linkText = v))}
        placeholder="Learn more"
      />

      <ColorField
        label="Background Color"
        value={backgroundColor}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.textColor = v))}
      />

      <SliderField
        label="Border Radius"
        value={borderRadius}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.borderRadius = v))}
        min={0}
        max={24}
        unit="px"
      />

      <CheckboxField
        label="Show Shadow"
        checked={showShadow}
        onChange={(v) => setProp((props: FeatureCardProps) => (props.showShadow = v))}
      />
    </SettingsSection>
  );
};

FeatureCard.craft = {
  displayName: "Feature Card",
  isCanvas: true,
  props: {
    title: "Feature Title",
    description: "Description of this feature",
    imageHeight: 60,
    categoryColor: "#F2819F",
    linkText: "Learn more",
    backgroundColor: "#ffffff",
    textColor: "#2C2C2B",
    borderRadius: 12,
    showShadow: false,
    textFontFamily: undefined,
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: FeatureCardSettings,
  },
};
